import { useState } from 'react';
import { EmailPreview, FilterChips, Icon, destacarVariaveis, type ChipOption } from '../../../components/ui';
import { LIMITE_ASSUNTO_CELULAR, cortarParaCelular } from '../entrega';

/**
 * Previa do e-mail no COMPUTADOR ou no CELULAR.
 *
 * Decisoes nao obvias:
 *  1. No celular a previa mostra primeiro a LINHA DA CAIXA DE ENTRADA
 *     (remetente, assunto cortado onde o aparelho corta, preheader) -- e ali
 *     que se decide se o e-mail e aberto. Depois vem o e-mail aberto numa
 *     largura de aparelho.
 *  2. O corte do assunto usa a mesma regra do medidor (40 caracteres):
 *     previa e medidor nunca discordam.
 *  3. So tokens do sistema de design; a moldura do aparelho e uma borda
 *     arredondada, sem imagem.
 */

type Dispositivo = 'computador' | 'celular';

const DISPOSITIVOS: readonly ChipOption<Dispositivo>[] = [
  { value: 'computador', label: 'Computador' },
  { value: 'celular', label: 'Celular' },
];

export function PreviaEmailDispositivo({
  from,
  subject,
  preheader,
  body,
  cta,
}: {
  readonly from: string;
  readonly subject: string;
  readonly preheader: string | undefined;
  readonly body: string;
  readonly cta: string | undefined;
}): JSX.Element {
  const [dispositivo, setDispositivo] = useState<Dispositivo>('computador');
  const nomeRemetente = from.split('<')[0]?.trim() ?? from;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-ink">Como chega na caixa de entrada</p>
        <FilterChips options={DISPOSITIVOS} value={dispositivo} onChange={setDispositivo} label="Dispositivo da prévia" tone="accent" />
      </div>
      {dispositivo === 'computador' ? (
        <EmailPreview from={from} subject={subject} preheader={preheader} body={body} cta={cta} />
      ) : (
        <div className="mx-auto w-full max-w-[22rem] rounded-[2rem] border-4 border-edge bg-page p-2 shadow-soft">
          <div className="mx-auto mb-2 h-1.5 w-16 rounded-full bg-edge" aria-hidden="true" />
          <div className="flex flex-col gap-2">
            <p className="px-2 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">Caixa de entrada</p>
            <div className="flex items-start gap-2 rounded-xl border border-line bg-surface px-3 py-2">
              <span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand/10 text-brand">
                <Icon name="email" size="xs" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-ink">{nomeRemetente}</p>
                <p className="text-xs font-medium text-ink">{destacarVariaveis(cortarParaCelular(subject))}</p>
                {preheader === undefined ? null : <p className="truncate text-[11px] text-ink-subtle">{preheader}</p>}
              </div>
            </div>
            <p className="px-2 text-[11px] text-ink-subtle">O celular mostra até {LIMITE_ASSUNTO_CELULAR} caracteres do assunto. Aberto:</p>
            <EmailPreview from={from} subject={subject} preheader={preheader} body={body} cta={cta} className="text-[13px]" />
          </div>
        </div>
      )}
    </div>
  );
}
